import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { FaSave, FaSchool, FaTimes } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/school-profile.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const EMPTY_FORM = {
  school_name: "",
  school_address: "",
  school_tag_line: "",
  school_reg_no: "",
  udise_no: "",
  school_estb_year: "",
  officail_contact_no1: "",
  officail_contact_no2: "",
  officail_email: "",
  website: "",
};

// Created By Cursor
function mapSchoolToForm(source) {
  const s = source || {};
  return {
    school_name: s.school_name || "",
    school_address: s.school_address || "",
    school_tag_line: s.school_tag_line || "",
    school_reg_no: s.school_reg_no || "",
    udise_no: s.udise_no || "",
    school_estb_year: s.school_estb_year || "",
    officail_contact_no1: s.officail_contact_no1 || "",
    officail_contact_no2: s.officail_contact_no2 || "",
    officail_email: s.officail_email || "",
    website: s.website || "",
  };
}

// Created By Cursor
function FormField({ id, label, value, onChange, type = "text", fullWidth = false, placeholder }) {
  return (
    <label className={`spd-field${fullWidth ? " spd-field--full" : ""}`} htmlFor={id}>
      <span className="spd-field__label">{label}</span>
      {type === "textarea" ? (
        <textarea
          id={id}
          className="spd-field__input spd-field__input--area"
          value={value}
          onChange={onChange}
          rows={3}
          placeholder={placeholder}
        />
      ) : (
        <input
          id={id}
          type={type}
          className="spd-field__input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
        />
      )}
    </label>
  );
}

export default function UpdateSchoolDetails() {
  const history = useHistory();
  const location = useLocation();
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Created By Cursor
  const loadSchool = useCallback(async () => {
    const fromState =
      location.state && location.state.school ? location.state.school : null;
    if (fromState) {
      setForm(mapSchoolToForm(fromState));
      setLoading(false);
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_error("School code is missing. Please login again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE()}getSchoolProfile.php`, {
        params: { school_code: schoolCode },
      });
      if (data?.api_status === "OK") {
        const row = Array.isArray(data.data) ? data.data[0] : data.data;
        setForm(mapSchoolToForm(row));
      } else {
        myToast.notify_warning(data?.api_remark || "Failed to load school details");
      }
    } catch (error) {
      myToast.notify_error(
        error?.response?.data?.api_remark ||
          "Network error. Please check your connection."
      );
    } finally {
      setLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    loadSchool();
  }, [loadSchool]);

  // Created By Cursor
  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  }, []);

  // Created By Cursor
  const handleCancel = useCallback(() => {
    history.push("/AdminMain/SchoolProfile");
  }, [history]);

  // Created By Cursor
  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_error("School code is missing. Please login again.");
        return;
      }
      if (!String(form.school_name || "").trim()) {
        myToast.notify_warning("School name is required");
        return;
      }

      setSaving(true);
      try {
        const { data } = await axios.get(`${API_BASE()}updateSchoolDetails.php`, {
          params: {
            school_code: schoolCode,
            school_name: form.school_name,
            address: form.school_address,
            tagline: form.school_tag_line,
            registration_no: form.school_reg_no,
            udise_no: form.udise_no,
            established_year: form.school_estb_year,
            contact1: form.officail_contact_no1,
            contact2: form.officail_contact_no2,
            email: form.officail_email,
            website: form.website,
          },
        });

        if (data?.api_status === "OK") {
          myToast.notify_success(data.api_remark || "School details updated");
          history.push("/AdminMain/SchoolProfile");
        } else {
          myToast.notify_warning(
            data?.api_remark || "Problem in updating school details"
          );
        }
      } catch (error) {
        myToast.notify_error(
          error?.response?.data?.api_remark ||
            "Network error while updating school details"
        );
      } finally {
        setSaving(false);
      }
    },
    [form, history]
  );

  if (!loggedIn) {
    return <Redirect to="/" />;
  }

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div
      className="content payroll-page sm-page sp-page spd-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Update School Details</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <span className="sp-hero__logo sp-hero__logo--fallback" aria-hidden>
              <FaSchool />
            </span>
            <div>
              <h1 className="sm-hero__title">Update School Details</h1>
              <p className="sm-hero__sub">
                Edit identity, contact, and registration information
              </p>
            </div>
          </div>
        </header>

        <form className="spd-form" onSubmit={handleSubmit}>
          <section className="spd-card">
            <h2 className="spd-card__title">Identity</h2>
            <div className="spd-grid">
              <FormField
                id="school_name"
                label="School name"
                value={form.school_name}
                onChange={handleChange}
                fullWidth
                placeholder="School name"
              />
              <FormField
                id="school_tag_line"
                label="Tag line"
                value={form.school_tag_line}
                onChange={handleChange}
                fullWidth
                placeholder="Short tag line"
              />
              <FormField
                id="school_address"
                label="Address"
                value={form.school_address}
                onChange={handleChange}
                type="textarea"
                fullWidth
                placeholder="Full school address"
              />
              <FormField
                id="school_estb_year"
                label="Established year"
                value={form.school_estb_year}
                onChange={handleChange}
                placeholder="e.g. 1998"
              />
              <FormField
                id="school_reg_no"
                label="Registration no."
                value={form.school_reg_no}
                onChange={handleChange}
              />
              <FormField
                id="udise_no"
                label="UDISE no."
                value={form.udise_no}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="spd-card">
            <h2 className="spd-card__title">Contact</h2>
            <div className="spd-grid">
              <FormField
                id="officail_contact_no1"
                label="Contact 1"
                value={form.officail_contact_no1}
                onChange={handleChange}
                type="tel"
              />
              <FormField
                id="officail_contact_no2"
                label="Contact 2"
                value={form.officail_contact_no2}
                onChange={handleChange}
                type="tel"
              />
              <FormField
                id="officail_email"
                label="Email"
                value={form.officail_email}
                onChange={handleChange}
                type="email"
                fullWidth
              />
              <FormField
                id="website"
                label="Website"
                value={form.website}
                onChange={handleChange}
                fullWidth
                placeholder="www.example.com"
              />
            </div>
          </section>

          <div className="sp-actions spd-actions">
            <button
              type="submit"
              className="sp-btn sp-btn--primary"
              disabled={saving}
            >
              <FaSave aria-hidden />
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              className="sp-btn sp-btn--ghost"
              onClick={handleCancel}
              disabled={saving}
            >
              <FaTimes aria-hidden />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
